/**
 * Listener sets for the state stores (W1-09 state-store).
 *
 * A failing listener never breaks the store: its error is reported (window `reportError`, or rethrown in a
 * microtask) and the remaining listeners still run. Unsubscribing twice is a no-op (core/util/types `Unsubscribe`).
 */

import type { Unsubscribe } from '../core/util/types';

/** Reports a listener failure without breaking the caller (the store stays consistent). */
export function reportListenerError(e: unknown): void {
  const g = globalThis as { reportError?: (e: unknown) => void };
  if (typeof g.reportError === 'function') {
    g.reportError(e);
    return;
  }
  queueMicrotask(() => {
    throw e;
  });
}

/** Calls each callback, isolating failures. */
export function callAll<T>(listeners: Iterable<(v: T) => void>, value: T): void {
  for (const cb of listeners) {
    try {
      cb(value);
    } catch (e) {
      reportListenerError(e);
    }
  }
}

/** An ordered listener set; the same function may subscribe several times (each subscription is independent). */
export class Emitter<T> {
  private readonly listeners = new Set<(v: T) => void>();

  get size(): number {
    return this.listeners.size;
  }

  subscribe(cb: (v: T) => void): Unsubscribe {
    const fn = (v: T): void => {
      cb(v);
    };
    this.listeners.add(fn);
    let active = true;
    return () => {
      if (!active) return;
      active = false;
      this.listeners.delete(fn);
    };
  }

  emit(value: T): void {
    if (this.listeners.size === 0) return;
    callAll([...this.listeners], value);
  }
}
